import React, { useEffect, useMemo, useRef, useState } from 'react'
import { BiChevronDown } from 'react-icons/bi'
import { AiOutlineSearch } from 'react-icons/ai'
import { FaCheck, FaTimes } from 'react-icons/fa'
import { useCurrent } from '../../hooks';
import AuthAvatar from './AuthAvatar';

// اختيار متعدد بنفس شكل MwSelector: بحث + صور + مسمى وظيفي، مع شيك بوكس لكل عنصر وشارات قابلة للحذف.
// القائمة تبقى مفتوحة أثناء الاختيار وتُغلق بالنقر خارجها أو بزر Escape.
const MwMultiSelector = ({
    label,
    _data,
    selectedItems,
    setSelectedItems,
    initalValue = 'Choose',
    withLabel = true,
    className,
    invalid,
    invalidMsg,
    disabled = false,
    showAvatar = true
}) => {
    const { currentLangId } = useCurrent();
    const isAr = currentLangId === 'ar';
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const wrapperRef = useRef(null);
    const searchRef = useRef(null);

    const selected = useMemo(() => (Array.isArray(selectedItems) ? selectedItems : []), [selectedItems]);
    const selectedIds = useMemo(() => new Set(selected.map((item) => String(item?.value ?? ''))), [selected]);
    const options = useMemo(
        () => (Array.isArray(_data) ? _data : []).filter((item) => item?.lang === currentLangId && item?.value),
        [_data, currentLangId]
    );
    const visibleOptions = useMemo(() => {
        const term = inputValue.trim().toLowerCase();
        if (!term) return options;
        return options.filter((item) =>
            String(item?.label || '').toLowerCase().includes(term) ||
            String(item?.subLabel || item?.jobTitle || '').toLowerCase().includes(term) ||
            String(item?.email || '').toLowerCase().includes(term)
        );
    }, [inputValue, options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!open) {
            setInputValue('');
            return;
        }
        searchRef.current?.focus();
    }, [open]);

    const toggleItem = (item) => {
        const id = String(item?.value ?? '');
        if (!id) return;
        setSelectedItems(
            selectedIds.has(id)
                ? selected.filter((entry) => String(entry?.value ?? '') !== id)
                : [...selected, item]
        );
    };

    const removeItem = (event, id) => {
        event.stopPropagation();
        if (disabled) return;
        setSelectedItems(selected.filter((entry) => String(entry?.value ?? '') !== String(id)));
    };

    const dir = isAr ? 'rtl' : 'ltr';

    return (
        <div
            ref={wrapperRef}
            className={`relative ${open ? 'z-[9999]' : 'z-10'} ${className || ''}`}
            onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}
        >
            {withLabel && <label className='text-xs text-slate-500 font-semibold mb-1.5 block'> {label} </label>}

            <div
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => { if (!disabled) setOpen(!open); }}
                onKeyDown={(event) => {
                    if (disabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpen(!open);
                    }
                }}
                className={`flex text-sm bg-white text-slate-700 w-full min-h-[38px] px-2 py-1 gap-2 items-center justify-between rounded-lg border transition-all duration-200 min-w-0 ${disabled ? 'cursor-not-allowed opacity-60 bg-slate-50' : 'cursor-pointer hover:border-indigo-400 hover:shadow-sm'} ${invalid ? 'border-red-500' : 'border-slate-200'} ${open ? 'border-indigo-400 ring-2 ring-indigo-100' : ''}`}
            >
                <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0" style={{ direction: dir }}>
                    {selected.length === 0 ? (
                        <span className="px-1 text-slate-400">{initalValue}</span>
                    ) : selected.map((item) => (
                        <span
                            key={String(item.value)}
                            className="inline-flex items-center gap-1.5 max-w-full ps-1 pe-1.5 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold"
                        >
                            {showAvatar && <AuthAvatar src={item.avatar} alt={item.label} size={18} fontSize={9} />}
                            <span className="truncate">{item.label}</span>
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={(event) => removeItem(event, item.value)}
                                    className="text-indigo-400 hover:text-red-500 transition-colors"
                                    aria-label={isAr ? `إزالة ${item.label}` : `Remove ${item.label}`}
                                >
                                    <FaTimes size={10} />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
                <BiChevronDown size={18} className={`text-slate-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </div>

            {open && (
                <div
                    className="bg-white border border-slate-200 absolute start-0 w-full mt-1 rounded-lg shadow-lg z-[9999] overflow-hidden"
                    style={{ direction: dir }}
                >
                    <div className='p-3 border-b border-slate-100'>
                        <div className='flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200'>
                            <AiOutlineSearch size={16} className='text-slate-400' />
                            <input
                                ref={searchRef}
                                type="text"
                                placeholder={isAr ? 'بحث...' : 'Search...'}
                                className='placeholder:text-slate-400 outline-none text-sm w-full bg-transparent'
                                value={inputValue}
                                onChange={(event) => setInputValue(event.target.value)}
                            />
                        </div>
                    </div>
                    <ul role="listbox" aria-multiselectable="true" className='max-h-60 overflow-y-auto'>
                        {visibleOptions.length === 0 ? (
                            <li className="px-4 py-3 text-xs text-slate-400 text-center">{isAr ? 'لا توجد نتائج' : 'No results'}</li>
                        ) : visibleOptions.map((item) => {
                            const isSelected = selectedIds.has(String(item.value));
                            const subLabel = item?.subLabel || item?.jobTitle;
                            return (
                                <li
                                    key={String(item.value)}
                                    role="option"
                                    aria-selected={isSelected}
                                    onClick={() => toggleItem(item)}
                                    className={`flex items-center gap-2.5 px-4 py-2.5 border-b border-slate-100 last:border-0 cursor-pointer text-sm transition-colors ${isSelected ? 'bg-indigo-50/70 text-indigo-700' : 'hover:bg-slate-50'}`}
                                >
                                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-300'}`}>
                                        {isSelected && <FaCheck size={9} />}
                                    </span>
                                    {showAvatar && <AuthAvatar src={item.avatar} alt={item.label} size={24} style={{ border: '1px solid #e2e8f0' }} />}
                                    <div className="flex-1 min-w-0">
                                        <div className={`truncate ${isSelected ? 'font-semibold' : ''}`}>{item.label}</div>
                                        {subLabel ? <div className="truncate text-[11px] text-slate-400">{subLabel}</div> : null}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-slate-100 bg-slate-50 text-xs">
                        <span className="text-slate-500 font-semibold">
                            {isAr ? `تم اختيار ${selected.length}` : `${selected.length} selected`}
                        </span>
                        <div className="flex items-center gap-3">
                            {selected.length > 0 && (
                                <button type="button" onClick={() => setSelectedItems([])} className="text-red-500 hover:text-red-700 font-semibold">
                                    {isAr ? 'مسح الكل' : 'Clear all'}
                                </button>
                            )}
                            <button type="button" onClick={() => setOpen(false)} className="text-indigo-600 hover:text-indigo-800 font-bold">
                                {isAr ? 'تم' : 'Done'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {invalidMsg && <div className='text-xs text-red-500 mt-1'>{invalidMsg}</div>}
        </div>
    )
}

export default MwMultiSelector
